import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Phone, MapPin, Calendar, Loader2, Lock, ClipboardList } from "lucide-react";
import { API_URL } from "../data/services";

export default function BookingsPage() {
  const [key, setKey] = useState(localStorage.getItem("am_admin_key") || "");
  const [bookings, setBookings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (e) => {
    e?.preventDefault();
    if (!key) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API_URL}/bookings`, { params: { key } });
      localStorage.setItem("am_admin_key", key);
      setBookings(
        [...res.data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      );
    } catch (err) {
      setBookings(null);
      setError(
        err.response?.status === 401
          ? "Wrong passcode. Please try again."
          : "Could not load bookings right now."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (localStorage.getItem("am_admin_key")) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-stone-900" data-testid="bookings-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10 sm:py-14">
        <div className="flex items-center justify-between gap-4 mb-10">
          <Link
            to="/"
            data-testid="back-home-link"
            className="inline-flex items-center gap-2 text-sm font-medium text-stone-600 hover:text-[#E85D75] transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4" /> Back to website
          </Link>
          <img src="/assets/logo.png" alt="Alpha & More" className="h-10 w-10 rounded-full object-cover" />
        </div>

        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3">
          <ClipboardList className="w-7 h-7 text-[#E85D75]" /> Booking List
        </h1>
        <p className="mt-2 text-sm text-stone-500">
          Every booking made on the website appears here, newest first.
        </p>

        <form onSubmit={load} className="mt-8 flex flex-wrap items-center gap-3">
          <div className="relative">
            <Lock className="w-4 h-4 text-stone-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Admin passcode"
              data-testid="bookings-passcode-input"
              className="rounded-full border border-stone-300 bg-white pl-11 pr-5 py-3 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-[#E85D75]/40"
            />
          </div>
          <button
            type="submit"
            data-testid="bookings-load-button"
            disabled={loading}
            className="rounded-full bg-[#E85D75] text-white text-sm font-semibold px-6 py-3 hover:bg-[#d64a63] disabled:opacity-60 transition-colors duration-200 inline-flex items-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {bookings ? "Refresh" : "View Bookings"}
          </button>
        </form>

        {error && (
          <p className="mt-4 text-sm font-medium text-red-600" data-testid="bookings-error">
            {error}
          </p>
        )}

        {bookings && (
          <div className="mt-8" data-testid="booking-list">
            <p className="text-sm text-stone-500 mb-4" data-testid="bookings-count">
              {bookings.length} booking{bookings.length === 1 ? "" : "s"}
            </p>
            {bookings.length === 0 ? (
              <div className="rounded-3xl bg-white border border-stone-200 p-10 text-center text-stone-500 text-sm" data-testid="bookings-empty">
                No bookings yet. New bookings will show up here automatically.
              </div>
            ) : (
              <div className="space-y-4">
                {bookings.map((b) => (
                  <div
                    key={b.id}
                    data-testid={`booking-row-${b.id}`}
                    className="rounded-3xl bg-white border border-stone-200 p-5 sm:p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-display text-lg font-semibold">{b.service}</p>
                        <p className="text-xs text-stone-400 mt-0.5">{b.category}</p>
                      </div>
                      <p className="text-xs text-stone-400">
                        {new Date(b.created_at).toLocaleString("en-IN", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm">
                      <span className="font-medium text-stone-800">{b.name}</span>
                      <a href={`tel:${b.phone}`} className="inline-flex items-center gap-1.5 text-stone-600 hover:text-[#E85D75]">
                        <Phone className="w-3.5 h-3.5" /> {b.phone}
                      </a>
                      {b.date && (
                        <span className="inline-flex items-center gap-1.5 text-stone-600">
                          <Calendar className="w-3.5 h-3.5" /> {b.date}{b.time ? ` · ${b.time}` : ""}
                        </span>
                      )}
                      {b.location && (
                        <span className="inline-flex items-center gap-1.5 text-stone-600">
                          <MapPin className="w-3.5 h-3.5" /> {b.location}
                        </span>
                      )}
                    </div>
                    {b.items?.length > 0 && (
                      <div className="mt-4 rounded-2xl bg-[#FAF9F6] border border-stone-100 p-4 space-y-1">
                        {b.items.map((i) => (
                          <div key={i.label} className="flex justify-between text-sm">
                            <span className="text-stone-600">
                              {i.label} × {i.qty}
                            </span>
                            <span className="font-medium">₹{i.price * i.qty}</span>
                          </div>
                        ))}
                        {b.total && (
                          <div className="flex justify-between text-sm font-semibold pt-2 border-t border-stone-200">
                            <span>Estimated Total</span>
                            <span>₹{b.total}</span>
                          </div>
                        )}
                      </div>
                    )}
                    {b.address && <p className="mt-3 text-sm text-stone-500">Address: {b.address}</p>}
                    {b.notes && <p className="mt-3 text-sm text-stone-500">Note: {b.notes}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
